import React from 'react';
import useTranslation from 'Hooks/useTranslation';
import { Checkbox, TextField, Tooltip } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ConfigurationContainer,
  ConfigurationItems,
  ExamDuration,
  CheckboxItem,
  checkboxText,
  headerText,
} from './style';

function ConfigurationTab({ formData, setFormData, setUnchanged }) {
  // Diccionario de traducciones
  const { t2 } = useTranslation('EditCourse');

  /**
   * Maneja el formato de la duración.
   * @param {number|undefined} duration - Duración en minutos
   */
  const formatDuration = duration => {
    if (!duration) {
      return '';
    }

    const date = new Date();
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    date.setHours(hours, minutes, 0);
    return date;
  };

  /**
   * Maneja el cambio de la fecha a horas.
   * @param {Date} date - Fecha a convertir.
   */
  const onChangeDuration = date => {
    // Convertir la fecha a horas y minutos
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Convertir a minutos
    const duration = hours * 60 + minutes;

    // Actualizar el estado
    setFormData({ ...formData, duration });
    setUnchanged(false);
  };

  /**
   * Maneja el cambio de estado de los checkboxes.
   * @param {React.ChangeEvent<HTMLInputElement>} event - Evento del checkbox
   */
  const handleChange = event => {
    const { name, checked } = event.target;

    switch (name) {
      case 'durationCheck':
        setFormData({ ...formData, duration: checked || null });
        setUnchanged(false);
        break;
      case 'randomOrderCheck':
        setFormData({ ...formData, isRandomQuestionOrder: checked });
        setUnchanged(false);
        break;
      case 'preventSwitchTabCheck':
        setFormData({ ...formData, preventTabSwitching: checked });
        setUnchanged(false);
        break;
      default:
        break;
    }
  };

  return (
    <ConfigurationContainer>
      <div style={headerText}>{t2('ExamConfigurationTitle')}</div>
      <ConfigurationItems>
        <ExamDuration>
          <CheckboxItem>
            <Checkbox
              color="MexlogGreen"
              name="durationCheck"
              checked={!!formData.duration}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <div style={checkboxText}>{t2('DurationLabel')}</div>
          </CheckboxItem>
        </ExamDuration>
        {!!formData.duration && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileTimePicker
              name="duration"
              ampm={false}
              openTo="hours"
              views={['hours', 'minutes']}
              inputFormat="HH:mm"
              mask="__:__"
              label={t2('ExamDuration')}
              value={formatDuration(formData?.duration)}
              onChange={onChangeDuration}
              color="success"
              renderInput={params => (
                <TextField
                  {...params}
                  sx={{ width: '9rem' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  color="MexlogGreen"
                  error={false}
                />
              )}
            />
          </LocalizationProvider>
        )}
        <Tooltip title={t2('random_question_order')}>
          <CheckboxItem>
            <Checkbox
              color="MexlogGreen"
              name="randomOrderCheck"
              checked={formData?.isRandomQuestionOrder}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <div style={checkboxText}>{t2('RandomQuestionOrderLabel')}</div>
          </CheckboxItem>
        </Tooltip>
        <Tooltip title={t2('prevent_switch_tab_tooltip')}>
          <CheckboxItem>
            <Checkbox
              color="MexlogGreen"
              name="preventSwitchTabCheck"
              checked={formData?.preventTabSwitching}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <div style={checkboxText}>{t2('prevent_switch_tab')}</div>
          </CheckboxItem>
        </Tooltip>
      </ConfigurationItems>
    </ConfigurationContainer>
  );
}

export default ConfigurationTab;
